import * as React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import {
  Avatar,
  Box,
  ButtonBase,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  CONFIDENT_SCORE,
  highlightParts,
  Suggestion,
} from 'components/ReportRequestV2/FuzzyFindDialog/nameRepair';

import styles from './RepairNamesDialog.module.css';

interface Props {
  /** The pasted name, used to label the list. */
  pasted: string;
  suggestions: Suggestion[];
  /** The search the suggestions came from, for highlighting. */
  query: string;
  highlighted: number;
  /** The confirmed catalog name, `null` if skipped, absent if not reviewed yet. */
  chosen: string | null | undefined;
  images: Map<number, string>;
  addedNames?: ReadonlySet<string>;
  onHighlight: (index: number) => void;
  onChoose: (name: string) => void;
}

const metadata = ({ entry }: Suggestion) => {
  const workspaces = Array.from(
    new Set(entry.products.map((p) => p.workspace)),
  );
  const reviews = entry.products.reduce((n, p) => n + p.reviews, 0);
  const category =
    entry.products.find((p) => p.category)?.category ?? 'Uncategorised';
  const where =
    workspaces.length > 2
      ? `${workspaces.slice(0, 2).join(', ')} +${workspaces.length - 2}`
      : workspaces.join(', ');
  return `${where} · ${category} · ${reviews.toLocaleString()} reviews`;
};

/** The closest catalog products for one name, as a keyboard-navigable list of cards. */
const SuggestionList: React.FC<Props> = ({
  pasted,
  suggestions,
  query,
  highlighted,
  chosen,
  images,
  addedNames,
  onHighlight,
  onChoose,
}) => {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

  React.useEffect(() => {
    // Keeps the highlighted card in view while arrowing through the list.
    refs.current[highlighted]?.scrollIntoView?.({ block: 'nearest' });
  }, [highlighted]);

  if (suggestions.length === 0) {
    return (
      <Box className={styles.noMatches} role="status">
        <SearchOffIcon fontSize="large" color="disabled" />
        <Typography variant="body1">
          No close matches for “{query.trim() || pasted}”.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try fewer words or another spelling, or skip this name.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      role="listbox"
      aria-label={`Matches for ${pasted}`}
      className={styles.suggestions}
    >
      {suggestions.map((s, i) => {
        const selected = chosen === s.entry.name;
        // Only ever a suggestion: nothing counts until a person confirms it.
        const suggested =
          chosen === undefined && i === 0 && s.score >= CONFIDENT_SCORE;
        const percent = Math.round(s.score * 100);
        const classes = [
          styles.card,
          selected && styles.cardSelected,
          i === highlighted && styles.cardHighlighted,
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <ButtonBase
            key={s.entry.name}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="option"
            aria-selected={selected}
            className={classes}
            onMouseEnter={() => onHighlight(i)}
            onClick={() => onChoose(s.entry.name)}
            tabIndex={-1}
          >
            <Avatar
              variant="rounded"
              src={images.get(s.entry.products[0].id)}
              alt=""
              className={styles.thumb}
            >
              {s.entry.name.charAt(0)}
            </Avatar>
            <Box className={styles.cardText}>
              <Typography variant="body2" className={styles.cardName}>
                {highlightParts(s.entry.name, query).map((part, j) =>
                  part.match ? (
                    <mark key={j} className={styles.mark}>
                      {part.text}
                    </mark>
                  ) : (
                    <React.Fragment key={j}>{part.text}</React.Fragment>
                  ),
                )}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="div"
              >
                {metadata(s)}
              </Typography>
            </Box>
            {suggested && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Suggested"
              />
            )}
            {addedNames?.has(s.entry.name) && (
              <Chip size="small" variant="outlined" label="In set" />
            )}
            <Box className={styles.meter} aria-label={`${percent}% match`}>
              <Typography variant="caption" color="text.secondary">
                {percent}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                color={s.score >= CONFIDENT_SCORE ? 'success' : 'primary'}
                className={styles.meterBar}
              />
            </Box>
            <CheckCircleIcon
              fontSize="small"
              color="primary"
              className={
                selected
                  ? styles.check
                  : `${styles.check} ${styles.checkHidden}`
              }
              aria-hidden
            />
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default SuggestionList;
